import { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { BadgePlus, Play, Pause } from "lucide-react";
import axios from "axios";
import DeleteConfirmation from "../../../components/DeleteConfirmation";

// ----- Reusable Components -----
const NoDataFound = () => (
  <div className="flex flex-col items-center justify-center h-full gap-2 py-6">
    <div className="w-10 h-10 rounded-full border-2 border-blue-200 flex items-center justify-center">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" strokeWidth="2">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    </div>
    <span className="text-gray-400 text-sm font-medium">No Data Found</span>
  </div>
);

const aiModels = [
  { name: "Kokoro-82M (Standard)", model: "hexgrad/Kokoro-82M" },
  { name: "Orpheus-3B (Standard)", model: "canopylabs/orpheus-3b-0.1-ft" },
  { name: "MiMo-V2.5-tts-voiceclone (Clone)", model: "XiaomiMiMo/MiMo-V2.5-tts-voiceclone" },
  { name: "MiMo-V2.5-tts-voicedesign (Design)", model: "XiaomiMiMo/MiMo-V2.5-tts-voicedesign" },
];

// ----- Static background sounds (with proper name for "None") -----
const backgroundSounds = [
  { name: "None", url: null },
  { name: "Ambiance", url: "https://ai-portal.webuyhouses.icu/sounds/ambiance.mp3" },
  { name: "Bank Ambience", url: "https://ai-portal.webuyhouses.icu/sounds/bank_ambience.mp3" },
  { name: "City Crowd", url: "https://ai-portal.webuyhouses.icu/sounds/city_crowd.mp3" },
  { name: "Library Ambience", url: "https://ai-portal.webuyhouses.icu/sounds/library_ambience.mp3" },
  { name: "NYC Street", url: "https://ai-portal.webuyhouses.icu/sounds/nyc_street.mp3" },
  { name: "Office Ambience 1", url: "https://ai-portal.webuyhouses.icu/sounds/office_ambience_1.mp3" },
  { name: "Office Ambience 2", url: "https://ai-portal.webuyhouses.icu/sounds/office_ambience_2.mp3" },
  { name: "People in Lounge", url: "https://ai-portal.webuyhouses.icu/sounds/people-in-lounge-1.mp3" },
  { name: "Soft Suspense Music", url: "https://ai-portal.webuyhouses.icu/sounds/soft-suspense-music-28126.mp3" },
  { name: "Typing 1", url: "https://ai-portal.webuyhouses.icu/sounds/typing_1.mp3" },
  { name: "Typing 2", url: "https://ai-portal.webuyhouses.icu/sounds/typing_2.mp3" },
  { name: "Waves White Noise", url: "https://ai-portal.webuyhouses.icu/sounds/waves-white-noise-9777.mp3" },
];

// ----- Main Component -----
export default function VoicemailDashboard() {
  const [loading, setLoading] = useState(false);
  const [voicemails, setVoicemails] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(25);

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [context, setContext] = useState("");
  const [selectedModel, setSelectedModel] = useState(aiModels[0].model);
  const [selectedBgSound, setSelectedBgSound] = useState(backgroundSounds[0].url); // default: None
  const [generating, setGenerating] = useState(false);

  // Preview player states
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  // Delete states
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedVoicemailId, setSelectedVoicemailId] = useState(null);

  // Detail modal (View)
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedVoicemail, setSelectedVoicemail] = useState(null);

  // Fetch voicemails
  const fetchVoicemails = async (newOffset = offset) => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/webhook/voicemails`, {
        params: { limit, offset: newOffset },
      });
      if (data?.success) {
        setVoicemails(data.data.data || []);
        setTotal(data.data.total || 0);
        setOffset(data.data.offset || 0);
      }
    } catch (error) {
      console.error("Error fetching voicemails:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVoicemails(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pagination
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit);

  const goToPage = (page) => {
    const newOffset = (page - 1) * limit;
    if (newOffset !== offset) fetchVoicemails(newOffset);
  };

  const handlePrevious = () => {
    if (currentPage > 1) goToPage(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) goToPage(currentPage + 1);
  };

  // Create new voicemail
  const handleGenerate = async () => {
    if (!context.trim()) {
      toast.warning("Please enter voicemail content.");
      return;
    }
    try {
      setGenerating(true);
      // Find the selected sound object to get its name
      const selectedSound = backgroundSounds.find((s) => s.url === selectedBgSound);
      const bgName = selectedSound ? selectedSound.name : "None";

      const payload = {
        content: context,
        model: selectedModel,
        bg_url: selectedBgSound, // send URL (null if None)
        bg_name: bgName, // send the display name
      };
      const { data } = await axios.post(`${process.env.REACT_APP_BASE_URL}/api/webhook/voicemails`, payload);
      if (data?.success) {
        setModalOpen(false);
        setContext("");
        setSelectedModel(aiModels[0].model);
        setSelectedBgSound(backgroundSounds[0].url); // reset to None
        // Stop any playing preview
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          setIsPlaying(false);
        }
        fetchVoicemails(0);
        toast.success("Voicemail generated successfully.");
      } else {
        toast.error(data?.message || "Failed to generate voicemail.");
      }
    } catch (error) {
      console.error("Error generating voicemail:", error);
      toast.error(error?.response?.data?.message || error?.message || "Error adding voicemail.");
    } finally {
      setGenerating(false);
    }
  };

  // Delete voicemail
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_BASE_URL}/api/webhook/voicemails/${id}`);
      toast.success("Voicemail deleted successfully.");
      fetchVoicemails(offset);
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error?.response?.data?.message || "Failed to delete voicemail.");
    } finally {
      setIsDeleteOpen(false);
      setSelectedVoicemailId(null);
    }
  };

  const toggleDeletePopup = (id) => {
    setSelectedVoicemailId(id);
    setIsDeleteOpen(!isDeleteOpen);
  };

  // View details
  const handleView = (vm) => {
    setSelectedVoicemail(vm);
    setDetailModalOpen(true);
  };

  // ---- Background preview handlers ----
  const togglePlayPreview = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      // If the sound changed, reload the audio element
      if (audioRef.current.src !== selectedBgSound) {
        audioRef.current.src = selectedBgSound;
        audioRef.current.load();
      }
      audioRef.current.play().catch((err) => {
        console.error("Preview play failed:", err);
        toast.warning("Could not play preview. Check the sound URL.");
      });
    }
    setIsPlaying(!isPlaying);
  };

  // When selectedBgSound changes, reset playing state
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      // Update src so that when user clicks play, it loads the new sound
      audioRef.current.src = selectedBgSound || "";
      audioRef.current.load();
    }
  }, [selectedBgSound]);

  const getModelName = (modelKey) => {
    if (!modelKey) return "—";
    const found = aiModels.find((m) => m.model === modelKey);
    return found ? found.name : modelKey;
  };

  const formatDate = (isoString) => {
    if (!isoString) return "—";
    return new Date(isoString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // Helper to get background sound name from URL (for display in list/detail)
  const getBackgroundSoundName = (url) => {
    if (!url) return "None";
    const found = backgroundSounds.find((s) => s.url === url);
    return found ? found.name : url;
  };

  return (
    <div>
      {loading ? (
        <div className="flex justify-center mt-10">
          <div className="loader ease-linear rounded-full border-8 border-t-8 border-gray-200 h-10 w-10 opacity-90"></div>
        </div>
      ) : (
        <div>
          {/* Header with Add button */}
          <div className="w-full flex justify-end">
            <div className="py-5 flex gap-3 px-5">
              <button className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800" type="button" onClick={() => setModalOpen(true)}>
                <BadgePlus className="inline mb-1 mr-1" size="16" />
                Add Voicemail
              </button>
            </div>
          </div>

          {voicemails && voicemails.length > 0 ? (
            <>
              <div className="px-5">
                <table className="min-w-full divide-y bg-back-dark divide-color-lines rounded-lg">
                  <thead className="min-w-full rounded-md divide-color-lines">
                    <tr>
                      <th scope="col" className="px-4 py-2 text-center text-sm text-slate-400 font-medium tracking-wider whitespace-nowrap">
                        ID
                      </th>
                      <th scope="col" className="px-4 py-2 text-center text-sm text-slate-400 font-medium tracking-wider whitespace-nowrap">
                        Model
                      </th>
                      <th scope="col" className="px-4 py-2 text-center text-sm text-slate-400 font-medium tracking-wider whitespace-nowrap">
                        Content
                      </th>
                      <th scope="col" className="px-4 py-2 text-center text-sm text-slate-400 font-medium tracking-wider whitespace-nowrap">
                        Background
                      </th>
                      <th scope="col" className="px-4 py-2 text-center text-sm text-slate-400 font-medium tracking-wider whitespace-nowrap">
                        File Name
                      </th>
                      <th scope="col" className="px-4 py-2 text-center text-sm text-slate-400 font-medium tracking-wider whitespace-nowrap">
                        Audio
                      </th>
                      <th scope="col" className="px-4 py-2 text-center text-sm text-slate-400 font-medium tracking-wider whitespace-nowrap">
                        Created At
                      </th>
                      <th scope="col" className="px-4 py-2 text-center text-sm text-slate-400 font-medium tracking-wider whitespace-nowrap">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-back-dark divide-y divide-color-lines">
                    {voicemails.map((vm) => (
                      <tr key={vm.id} className="hover:bg-gray-900 text-xs">
                        <td className="px-4 py-2 whitespace-wrap">{vm.id}</td>
                        <td className="px-4 py-2 whitespace-wrap">{getModelName(vm.model)}</td>
                        <td className="px-4 py-2 whitespace-wrap max-w-xs truncate" title={vm.content}>
                          {vm.content}
                        </td>
                        <td className="px-4 py-2 whitespace-wrap">{getBackgroundSoundName(vm.background_sound_url)}</td>
                        <td className="px-4 py-2 whitespace-wrap">{vm.file_name || "—"}</td>
                        <td className="px-4 py-2 whitespace-wrap">{vm.audio_url ? <audio controls src={vm.audio_url} className="w-32 h-6" /> : "N/A"}</td>
                        <td className="px-4 py-2 whitespace-wrap">{formatDate(vm.created_at)}</td>
                        <td className="px-4 py-2 whitespace-wrap flex gap-4 items-center">
                          <button className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 mr-2" onClick={() => handleView(vm)}>
                            View
                          </button>
                          <button className="text-white bg-red-700 hover:bg-red-800 focus:ring-4 font-medium rounded-lg text-sm px-3 py-2 dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-800" onClick={() => toggleDeletePopup(vm.id)}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination controls */}
              {total > 0 && (
                <div className="flex items-center justify-between mt-4 text-xs text-gray-400 px-5 py-2">
                  <span>
                    Showing {offset + 1} – {Math.min(offset + limit, total)} of {total}
                  </span>
                  <div className="flex gap-2">
                    <button onClick={handlePrevious} disabled={currentPage === 1} className={`px-3 py-1 rounded-md border ${currentPage === 1 ? "bg-gray-700 text-gray-500 cursor-not-allowed" : "bg-gray-800 text-gray-300 hover:bg-gray-700"} transition-colors`}>
                      Previous
                    </button>
                    <span className="px-3 py-1 bg-blue-900 text-blue-300 rounded-md">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button onClick={handleNext} disabled={currentPage === totalPages} className={`px-3 py-1 rounded-md border ${currentPage === totalPages ? "bg-gray-700 text-gray-500 cursor-not-allowed" : "bg-gray-800 text-gray-300 hover:bg-gray-700"} transition-colors`}>
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center text-center text-xl pt-10">
              <div>
                No Voicemails found.
                <div className="block text-base mt-5 mb-10">Start by creating a new voicemail.</div>
                <div className="flex items-center justify-center">
                  <div onClick={() => setModalOpen(true)} className="rounded-lg text-center overflow-hidden bg-blue-700 p-4 hover:bg-blue-900 cursor-pointer">
                    <BadgePlus className="inline mb-2" size="30" />
                    <p className="text-base">Create New Voicemail</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ----- Add Voicemail Modal ----- */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-100">Generate New Voicemail</h2>
              <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-200">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Content textarea */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Voicemail Content</label>
                <textarea rows={4} className="w-full border border-gray-600 rounded-lg px-3 py-2 text-sm bg-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter the text to be spoken..." value={context} onChange={(e) => setContext(e.target.value)} />
              </div>

              {/* Model selection */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">TTS Model</label>
                <select className="w-full border border-gray-600 rounded-lg px-3 py-2 text-sm bg-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)}>
                  {aiModels.map((model) => (
                    <option key={model.model} value={model.model}>
                      {model.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Background Sound selection with preview */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Background Sound</label>
                <div className="flex gap-2">
                  <select className="flex-1 border border-gray-600 rounded-lg px-3 py-2 text-sm bg-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" value={selectedBgSound || ""} onChange={(e) => setSelectedBgSound(e.target.value || null)}>
                    {backgroundSounds.map((sound) => (
                      <option key={sound.name} value={sound.url || ""}>
                        {sound.name}
                      </option>
                    ))}
                  </select>
                  <button type="button" onClick={togglePlayPreview} disabled={!selectedBgSound} className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${!selectedBgSound ? "bg-gray-600 text-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 text-white"}`} title={selectedBgSound ? "Play preview" : "No sound selected"}>
                    {isPlaying ? <Pause size={18} /> : <Play size={18} />}
                  </button>
                </div>
                {/* Hidden audio element for preview */}
                <audio ref={audioRef} onEnded={() => setIsPlaying(false)} onPause={() => setIsPlaying(false)} className="hidden" />
                <p className="text-xs text-gray-400 mt-1">{selectedBgSound ? "Click play to preview the selected sound." : "Select a sound to preview."}</p>
              </div>

              {/* Action buttons */}
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setModalOpen(false)} className="px-4 py-2 text-sm rounded-md border border-gray-600 hover:bg-gray-700 transition-colors text-gray-300">
                  Cancel
                </button>
                <button onClick={handleGenerate} disabled={generating || !context.trim()} className={`px-4 py-2 text-sm rounded-md text-white ${generating || !context.trim() ? "bg-gray-500 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"} transition-colors`}>
                  {generating ? "Generating..." : "Generate & Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ----- Detail Modal (View) ----- */}
      {detailModalOpen && selectedVoicemail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-100">Voicemail Details</h2>
              <button onClick={() => setDetailModalOpen(false)} className="text-gray-400 hover:text-gray-200">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="space-y-2 text-gray-300">
              <p>
                <strong>ID:</strong> {selectedVoicemail.id}
              </p>
              <p>
                <strong>Model:</strong> {getModelName(selectedVoicemail.model)}
              </p>
              <p>
                <strong>Content:</strong> {selectedVoicemail.content}
              </p>
              <p>
                <strong>Background Sound:</strong> {getBackgroundSoundName(selectedVoicemail.background_sound_url)}
              </p>
              <p>
                <strong>File Name:</strong> {selectedVoicemail.file_name || "—"}
              </p>
              <p>
                <strong>Created At:</strong> {formatDate(selectedVoicemail.created_at)}
              </p>
              <div>
                <strong>Audio:</strong>
                {selectedVoicemail.audio_url ? <audio controls src={selectedVoicemail.audio_url} className="w-full mt-2" /> : " N/A"}
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={() => setDetailModalOpen(false)} className="px-4 py-2 text-sm rounded-md border border-gray-600 hover:bg-gray-700 transition-colors text-gray-300">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ----- Delete Confirmation ----- */}
      {selectedVoicemailId && (
        <DeleteConfirmation
          isOpen={isDeleteOpen}
          onClose={() => {
            setIsDeleteOpen(false);
            setSelectedVoicemailId(null);
          }}
          title={"Are you sure you want to delete this voicemail?"}
          id={selectedVoicemailId}
          fetchDataFromApi={() => fetchVoicemails(offset)}
          deleteMethod={handleDelete}
        />
      )}
    </div>
  );
}
